/**
 * Fase 38 — detalle de un finding.
 *
 * Muestra evidence inline (quotes con link al SessionDetail filtrado al
 * turno), proposed_fix con diff opcional, y permite cambiar status / notes.
 * Botón de re-run en la sesión origen para reabrir el pipeline (idempotente).
 */
import { useState, type ReactElement } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  enqueueAnalyzeRun,
  fetchFinding,
  promoteFinding,
  recordFindingAction,
  updateFinding,
  type FindingDetail,
  type FindingEvidenceQuote,
  type UpdateFindingInput,
} from '../../api/admin/findings.js';
import { shortId } from '../../lib/format.js';
import { GATED_CONTENT_PLACEHOLDER } from '../../lib/privacy.js';

const STATUS_OPTIONS = ['new', 'triaged', 'accepted', 'rejected', 'shipped', 'duplicate'] as const;

function SeverityBadge({ value }: { value: number }): ReactElement {
  const cls =
    value >= 5
      ? 'badge b-danger'
      : value === 4
        ? 'badge b-warn'
        : value === 3
          ? 'badge b-info'
          : 'badge';
  return <span className={cls}>S{value}</span>;
}

function QuoteCard({
  quote,
  sessionId,
}: {
  quote: FindingEvidenceQuote;
  sessionId: string;
}): ReactElement {
  return (
    <div className="card" style={{ padding: 12, marginBottom: 8 }}>
      <div className="flex" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
        <span className="mono t-meta">
          turn {quote.turn_index} · {quote.speaker}
        </span>
        <Link to={`/sessions/${sessionId}?turn=${quote.turn_index}`} className="t-meta">
          ver waterfall →
        </Link>
      </div>
      <blockquote
        style={{
          margin: 0,
          padding: '4px 12px',
          borderLeft: '3px solid var(--border)',
          fontFamily: 'inherit',
        }}
      >
        {quote.text}
      </blockquote>
    </div>
  );
}

function ProposedFixCard({ fix }: { fix: FindingDetail['proposedFix'] }): ReactElement | null {
  if (!fix) return null;
  return (
    <section className="card" style={{ padding: 16, marginTop: 16 }}>
      <h3 style={{ marginTop: 0 }}>Fix propuesto</h3>
      <div className="mono t-meta">target: {fix.target}</div>
      {fix.scope ? (
        <pre style={{ background: 'var(--bg-mute)', padding: 8, overflow: 'auto' }}>
          {JSON.stringify(fix.scope, null, 2)}
        </pre>
      ) : null}
      {fix.before ? (
        <>
          <div className="t-meta">Before:</div>
          <pre style={{ background: 'var(--bg-mute)', padding: 8, overflow: 'auto' }}>{fix.before}</pre>
        </>
      ) : null}
      {fix.after ? (
        <>
          <div className="t-meta">After:</div>
          <pre style={{ background: 'var(--bg-mute)', padding: 8, overflow: 'auto' }}>{fix.after}</pre>
        </>
      ) : null}
      <div style={{ marginTop: 8 }}>{fix.rationale}</div>
    </section>
  );
}

export function FindingDetailPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState<string>('');
  const [notesDirty, setNotesDirty] = useState(false);

  const query = useQuery({
    queryKey: ['admin', 'finding', id],
    queryFn: () => fetchFinding(id ?? ''),
    enabled: typeof id === 'string' && id.length > 0,
  });

  if (!notesDirty && query.data && query.data.reviewerNotes !== null && notes === '') {
    setNotes(query.data.reviewerNotes);
  }

  const mutation = useMutation({
    mutationFn: (patch: UpdateFindingInput) => updateFinding(id ?? '', patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'finding', id] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'findings'] });
    },
  });

  const rerun = useMutation({
    mutationFn: () => enqueueAnalyzeRun(query.data!.sessionId),
  });

  const [expected, setExpected] = useState('');
  const promote = useMutation({
    mutationFn: () =>
      promoteFinding(id ?? '', {
        scenario: {
          finding_title: query.data!.title,
          description: query.data!.description,
          evidence: query.data!.evidence,
        },
        expectedBehavior: expected.trim() || (query.data!.title ?? ''),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'regression'] });
    },
  });

  const markShipped = useMutation({
    mutationFn: () =>
      recordFindingAction(id ?? '', {
        actionType: query.data?.proposedFix?.target ?? 'manual',
        target: query.data?.proposedFix?.scope ?? { moduleSlug: query.data?.moduleSlug ?? null },
        diff: {
          before: query.data?.proposedFix?.before ?? null,
          after: query.data?.proposedFix?.after ?? null,
        },
        ...(query.data?.proposedFix?.rationale
          ? { notes: query.data.proposedFix.rationale }
          : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'finding', id] });
    },
  });

  if (query.isPending) return <div className="loading">Cargando…</div>;
  if (query.isError) return <div className="loading">Error: {query.error.message}</div>;
  const finding = query.data;

  return (
    <>
      <div className="page-header" style={{ alignItems: 'flex-start' }}>
        <div>
          <button type="button" className="btn-link" onClick={() => navigate('/findings')}>
            ← findings
          </button>
          <h1 className="page-title" style={{ marginTop: 4 }}>
            {finding.gated ? GATED_CONTENT_PLACEHOLDER : finding.title}
          </h1>
          <div className="flex gap-2 t-meta" style={{ marginTop: 4, alignItems: 'center' }}>
            <SeverityBadge value={finding.severity} />
            <span className="mono">{finding.category ?? '—'}</span>
            <span className="mono">module: {finding.moduleSlug}</span>
            <span>· run {shortId(finding.runId, 8)}</span>
            <span>·</span>
            <Link to={`/sessions/${finding.sessionId}`}>session {shortId(finding.sessionId, 8)}</Link>
            {finding.regressionOf && (
              <>
                <span>·</span>
                <Link
                  to={`/findings/${finding.regressionOf}`}
                  title="Este finding fue matcheado contra un fix shipped previo — el bug volvió"
                >
                  <span className="badge b-danger">🚨 regression of {shortId(finding.regressionOf, 8)}</span>
                </Link>
              </>
            )}
          </div>
        </div>
        <button
          type="button"
          className="btn"
          onClick={() => rerun.mutate()}
          disabled={rerun.isPending || finding.gated === true}
          title={
            finding.gated
              ? 'Módulo sensible — el re-run está bloqueado por el gate de privacidad'
              : undefined
          }
        >
          {rerun.isPending ? 'Encolando…' : 're-run analyze'}
        </button>
      </div>

      <section className="card" style={{ padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>Descripción</h3>
        <div style={{ whiteSpace: 'pre-wrap' }}>
          {finding.gated ? (
            <span className="t-meta">{GATED_CONTENT_PLACEHOLDER}</span>
          ) : (
            finding.description
          )}
        </div>
      </section>

      <section className="card" style={{ padding: 16, marginTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>Evidence — quotes</h3>
        {finding.evidence === null ? (
          <div className="t-meta">{GATED_CONTENT_PLACEHOLDER}</div>
        ) : finding.evidence.quotes.length === 0 ? (
          <div className="empty">Sin quotes (esto no debería pasar — Critic filtra evidence vacía).</div>
        ) : (
          finding.evidence.quotes.map((q, i) => (
            <QuoteCard key={`${q.turn_index}-${i}`} quote={q} sessionId={finding.sessionId} />
          ))
        )}
        {finding.evidence?.metrics ? (
          <div className="t-meta" style={{ marginTop: 8 }}>
            metrics:{' '}
            <code className="mono">{JSON.stringify(finding.evidence.metrics)}</code>
          </div>
        ) : null}
      </section>

      <ProposedFixCard fix={finding.proposedFix} />

      <section className="card" style={{ padding: 16, marginTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>Acciones</h3>
        <div className="flex gap-2" style={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <button
            type="button"
            className="btn"
            onClick={() => markShipped.mutate()}
            disabled={markShipped.isPending || finding.status === 'shipped'}
            title="Registra una analyze_finding_actions con el proposed_fix actual y marca el finding como shipped"
          >
            {markShipped.isPending ? 'Aplicando…' : 'Mark fix shipped'}
          </button>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 320, flexGrow: 1 }}>
            <label className="label" htmlFor="promote-expected">
              Promote a regression set — expected behavior
            </label>
            <div className="flex gap-2">
              <input
                id="promote-expected"
                className="input"
                value={expected}
                onChange={(e) => setExpected(e.target.value)}
                placeholder={finding.title ?? ''}
                style={{ flexGrow: 1 }}
              />
              <button
                type="button"
                className="btn"
                onClick={() => promote.mutate()}
                disabled={promote.isPending || finding.gated === true}
                title={
                  finding.gated
                    ? 'Módulo sensible — la evidence está gateada, no hay scenario que promover'
                    : undefined
                }
              >
                {promote.isPending ? 'Promoviendo…' : 'Promote'}
              </button>
            </div>
            {promote.isSuccess ? (
              <span className="t-meta">Agregado al dataset · {promote.data.datasetId}</span>
            ) : null}
          </div>
        </div>
      </section>

      <section className="card" style={{ padding: 16, marginTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>Triage</h3>
        <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              type="button"
              className={`chip${finding.status === s ? ' is-on' : ''}`}
              onClick={() => mutation.mutate({ status: s })}
              disabled={mutation.isPending || finding.status === s}
            >
              {s}
            </button>
          ))}
        </div>
        <div style={{ marginTop: 12 }}>
          <label className="label" htmlFor="finding-notes">
            Reviewer notes
          </label>
          <textarea
            id="finding-notes"
            className="input"
            rows={4}
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
              setNotesDirty(true);
            }}
            placeholder="Por qué se acepta/rechaza, o qué fix aplicar"
          />
          <div style={{ marginTop: 8 }}>
            <button
              type="button"
              className="btn"
              disabled={mutation.isPending || !notesDirty}
              onClick={() => {
                mutation.mutate({ reviewerNotes: notes });
                setNotesDirty(false);
              }}
            >
              Guardar notas
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
